/**
 * Circular dependency prevention:
 * - useBasket
 */
import { computed, readonly } from 'vue'
import { cloneDeep, isNil } from 'lodash'
import { v4 as uuidv4 } from 'uuid'

import { DateTimeHelper } from '@/helpers/DateTime'

import { ADULT, MINOR, SENIOR, GENDER } from '@/config/constants'

import useBasket from '@/hooks/useBasket'
import useContract from '@/hooks/useContract'
import usePersonSpecificProduct from '@/hooks/usePersonSpecificProduct'
import useProduct from '@/hooks/useProduct'
import useI18n from '@/hooks/useI18n'

// Hooks
const { contractPersonsWithDetails, getCaveats } = useContract()
const { getSelectedProductsOfPerson } = usePersonSpecificProduct()
const { getCategoryIdFromProduct } = useProduct()
const { t } = useI18n()

const persons = computed(() => {
  const { basket } = useBasket()
  return cloneDeep(basket.persons || [])
})

const firstPersonId = computed(() => {
  return persons.value.length > 0 ? persons.value[0].personId : undefined
})

const personsDetails = computed(() => {
  const { basket } = useBasket()

  const data = {}
  /*persons.value*/ basket.persons.forEach((person, index) => {
    const __number = index + 1
    const __selectedProducts = getSelectedProductsOfPerson(person.products.products)
    const __recommendations = person.recommendations.map(productId => {
      return Object.assign({}, person.products.products[productId], {
        productId,
        categoryId: getCategoryIdFromProduct(productId),
      })
    })
    const __age = DateTimeHelper.getAge(person.personData.dateOfBirth)

    let icon
    if (__age >= 18) {
      icon = person.personData.gender === GENDER.MALE ? 'man' : 'woman'
    } else if (__age < 18 && __age >= 5) {
      icon = person.personData.gender === GENDER.MALE ? 'boy' : 'girl'
    } else if (__age < 5 && __age >= 0) {
      icon = 'baby'
    } else {
      icon = 'unborn'
    }

    data[person.personId] = {
      number: index + 1,
      age: __age,
      icon,
      firstName: person.personData.firstName ?? t('person.person', { number: __number }),
      total: parseFloat(
        __selectedProducts
          .reduce((total, product) => {
            total += product.price.price
            return total
          }, 0)
          .toFixed(2)
      ),
      caveats: getCaveats(person),

      // @note don't use `products` or `recommendations` as a name, as it's already in use by data from backend
      selectedProducts: __selectedProducts,
      productRecommendations: __recommendations,
    }
  })

  return data
})

const personsWithDetails = computed(() => {
  const { basket } = useBasket()

  const people = (basket.persons || []).map(person => {
    return Object.assign({}, person, personsDetails.value[person.personId])
  })

  return readonly(people)
})

const signees = computed(() => {
  const { basket } = useBasket()

  if (!persons.value) return []

  const __persons = cloneDeep(personsWithDetails.value)
  // According to business only persons with contractModified need to sign in all cases
  const personsToSign = __persons.filter(person => person.contractModified)
  // Head should always be the last signee
  const ownerIndex = personsToSign.findIndex(person => person.personId === basket.contractOwner.personId)
  if (ownerIndex > -1) {
    personsToSign.push(personsToSign.splice(ownerIndex, 1)[0])
  }
  return personsToSign
})

async function createPerson(personId = uuidv4()) {
  const { getDefaultContractStartDate } = useBasket()

  const defaultContracStartDate = await getDefaultContractStartDate()

  return {
    order: null,
    contractModified: true,
    collectivePersonGroup: null,
    immigrant: false,
    kvgContractStartDate: defaultContracStartDate,
    vvgContractStartDate: defaultContracStartDate,
    personData: {
      dateOfBirth: null,
      firstName: null,
      lastName: null,
      gender: null,
    },
    personId,
  }
}

function getPerson(personId) {
  return persons.value.find(p => p.personId === personId)
}

function getPersonsByMutability(__persons = personsWithDetails.value) {
  const data = { mutable: [], immutable: [] }
  __persons.forEach(person => {
    // As immutable persons are always existing customers, we need to get them from the contractPersonsWithDetails as only there we get the selectedProducts for each one
    // Edge case: getFamily call does not load contracts, so we need to take person instead for login
    const contractPerson = contractPersonsWithDetails.value.length
      ? contractPersonsWithDetails.value.find(__contractPerson => __contractPerson.personId === person.partnerNumber)
      : person
    person.contractModified ? data.mutable.push(person) : data.immutable.push(contractPerson)
  })

  return data
}

function getPersonWithDetails(personId) {
  return personsWithDetails.value.find(p => p.personId === personId)
}

/**
 * filterPersonsToValidate returns all the persons which need to be validated to proceed
 * As a basket on for an existing customer (from the service portal channel switch) always
 * contains all the persons of the contract, only the one's that have their contract modified
 * needs to be validated
 * @param {basket} payload
 * @return {Array} persons
 */
function filterPersonsToValidate(payload) {
  const { existingCustomer, persons: __persons } = payload
  return existingCustomer ? __persons.filter(p => p.contractModified) : __persons
}

function getAgeBracketFromPerson(personId, seniorAge = 65) {
  const person = getPersonWithDetails(personId)

  if (isNil(person.age)) {
    return null
  } else if (person.age < 18) {
    return MINOR
  } else if (person.age >= seniorAge) {
    return SENIOR
  } else {
    return ADULT
  }
}

function getWorkSituationFields({ isVVGOnly, isKVGOnly }, workSituationData) {
  if (isNil(isVVGOnly) || isNil(isKVGOnly)) {
    throw new Error('Missing required parameters')
  }

  const fields = []

  if (askWorkSituation(isVVGOnly)) {
    fields.push({ name: 'workSituation', required: true })
  }

  if (askSecondaryEmployment(isKVGOnly)) {
    fields.push({ name: 'secondaryEmployment', required: true })
    if (workSituationData.secondaryEmployment === true) {
      fields.push({ name: 'professionSecondaryEmployment', required: true })
    }
  }

  return fields
}

function askWorkSituation(isVVGOnly) {
  return !isVVGOnly
}

function askSecondaryEmployment(isKVGOnly) {
  return !isKVGOnly
}

function getFirstName(personId) {
  console.warn('DEPRECATED, use `personsDetails`')

  // const t = app.appContext.config.globalProperties.$t

  const index = getPersonIndex(personId)
  const person = persons.value[index]
  return person.personData.firstName ?? t('person.person', { number: index + 1 })
}

/**
 * @param {String} personId
 * @returns {Number} index - index of the person in the basket
 */
function getPersonIndex(personId) {
  return persons.value.findIndex(p => p.personId === personId)
}

function getPersonByPartnerNumber(partnerNumber) {
  return persons.value.find(p => p.partnerNumber === partnerNumber)
}

function isUnborn(personId) {
  const person = getPerson(personId)
  return person.age < 0
}

function addOrderToPersons(unorderedPersons) {
  return unorderedPersons.map((person, index) => {
    return {
      ...person,
      order: index + 1,
    }
  })
}

export default function usePerson() {
  return {
    // COMPUTED
    firstPersonId,
    persons,
    personsDetails,
    personsWithDetails,
    signees,

    // METHODS
    addOrderToPersons,
    createPerson,
    filterPersonsToValidate,
    getAgeBracketFromPerson,
    getFirstName,
    getPerson,
    getPersonByPartnerNumber,
    getPersonsByMutability,
    getPersonWithDetails,
    getPersonIndex,

    getWorkSituationFields,
    isUnborn,
  }
}
